import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useRef, useCallback } from 'react';
/** Poll interval in ms. */
const POLL_INTERVAL = 5000;
/**
 * Overlay shown when a file's URL is reachable but not browser-playable.
 * Polls /encoding/progress?id={fileId} to show queue position or encoding
 * progress. Once done, reloads the page. Clickable to download the original.
 */
export const EncodingProgressOverlay = React.memo(function EncodingProgressOverlay({ visible, fileId, downloadUrl, title, fileTypeName, fileSize, fileIconUrl, onSwitchToProxy, }) {
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(false);
    const timerRef = useRef(undefined);
    const mountedRef = useRef(true);
    const progressBarRef = useRef(null);
    const poll = useCallback(async () => {
        if (!fileId)
            return;
        try {
            const resp = await fetch(`/encoding/progress?id=${fileId}`);
            if (!resp.ok) {
                setError(true);
                return;
            }
            const data = await resp.json();
            if (!mountedRef.current)
                return;
            if (data.status === 'done') {
                // Proxy is ready — reload to pick up the new source
                window.location.reload();
                return;
            }
            setStatus(data);
            setError(false);
        }
        catch {
            if (mountedRef.current)
                setError(true);
        }
    }, [fileId]);
    useEffect(() => {
        mountedRef.current = true;
        if (!visible || !fileId)
            return;
        // Poll immediately, then on interval
        poll();
        timerRef.current = setInterval(poll, POLL_INTERVAL);
        return () => {
            mountedRef.current = false;
            if (timerRef.current)
                clearInterval(timerRef.current);
        };
    }, [visible, fileId, poll]);
    // Imperatively update the progress-bar-circular custom element attribute
    // because React doesn't reliably sync attributes on custom elements.
    const progressValue = status?.status === 'processing' ? (status.progress ?? 0) : 0;
    useEffect(() => {
        if (progressBarRef.current) {
            progressBarRef.current.setAttribute('progress', String(progressValue));
        }
    }, [progressValue]);
    if (!visible)
        return null;
    const isQueued = status?.status === 'queued';
    const isProcessing = status?.status === 'processing';
    let statusText;
    if (error || !status) {
        statusText = 'This file is being prepared for playback.';
    }
    else if (isQueued) {
        statusText = status.position === 1
            ? 'This file is next in the queue.'
            : `This file is in position ${status.position} in the queue.`;
    }
    else if (isProcessing) {
        statusText = 'This file is being optimised for playback.';
    }
    else {
        statusText = 'Preparing file for playback...';
    }
    const iconSrc = fileIconUrl || '/img/Unknown_Doc.svg';
    const content = (_jsx("div", { className: "hekate-player-encoding-overlay", children: _jsxs("div", { className: "hekate-player-encoding-box", children: [_jsxs("div", { className: "hekate-player-encoding-header", children: [title && _jsx("h2", { className: "hekate-player-encoding-title", children: title }), !title && _jsx("h2", { className: "hekate-player-encoding-title", children: "Optimizing for playback" })] }), _jsx("div", { className: "hekate-player-encoding-progress-area", children: React.createElement('progress-bar-circular', {
                        ref: (el) => { progressBarRef.current = el; },
                        width: '80',
                        height: '80',
                        'image-url': iconSrc,
                        progress: String(progressValue),
                    }) }), _jsxs("div", { className: "hekate-player-encoding-status", children: [_jsx("p", { className: "hekate-player-encoding-status-text", children: statusText }), isProcessing && (_jsxs("p", { className: "hekate-player-encoding-percent", children: [progressValue, "%"] })), fileTypeName && (_jsxs("p", { className: "hekate-player-encoding-meta", children: [fileTypeName, fileSize != null && fileSize > 0 && (_jsxs(_Fragment, { children: [" \u00B7 ", formatSize(fileSize)] }))] }))] }), onSwitchToProxy && (_jsx("button", { className: "hekate-player-switch-proxy-btn", onClick: (e) => { e.preventDefault(); onSwitchToProxy(); }, type: "button", children: "Switch to Proxy" })), downloadUrl && (_jsx("div", { className: "hekate-player-encoding-download", children: "Click to download the original file" }))] }) }));
    if (downloadUrl) {
        return (_jsx("div", { className: "hekate-player-download-overlay hekate-player-download-overlay--visible", children: _jsx("a", { href: downloadUrl, className: "hekate-player-encoding-link", children: content }) }));
    }
    return (_jsx("div", { className: "hekate-player-download-overlay hekate-player-download-overlay--visible", children: content }));
});
function formatSize(bytes) {
    if (bytes < 1024)
        return `${bytes} bytes`;
    if (bytes < 1024 * 1024)
        return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024)
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
